// components/ui/StageList.js — vertical workflow list for long-running tasks.
//
// Used by quiz/content generation while the async-job worker walks
// through phases. Reads as a workflow, not a status list:
//   - vertical connector line linking the dots
//   - active row gets a ring-pulse on the dot, optional progress bar,
//     optional `detail` sub-line that ticks with current micro-state
//   - elapsed time in a quiet corner if `elapsedSec` is provided
//
// Aesthetic: still quiet — no spinners, no icons, no celebration.
// The motion is deliberate (dot ring-pulse, progress bar fill), not
// decorative.
//
// Stage shape: { id, label, state, progress?, detail? } where:
//   - state: 'done' | 'active' | 'pending'
//   - progress (optional): { done: number, total: number } shown as
//     "n / m" right-aligned AND as a thin progress bar under the row
//   - detail (optional): string shown small under the row (only when
//     state === 'active')
//
// Top-level prop:
//   - elapsedSec (optional): number — small "~Ns" in the header strip
import React from 'react';
import LevelDot from './LevelDot';

function dotColor(state) {
  if (state === 'done')   return 'good';
  if (state === 'active') return 'info';
  return 'faint';
}

function rowText(state) {
  if (state === 'pending') return 'text-faint';
  if (state === 'active')  return 'text-ink font-medium';
  return 'text-mutedink';
}

// Tailwind-arbitrary keyframes via inline style — keeps StageList
// self-contained without touching tailwind.config.
const pulseRingStyle = {
  animation: 'stage-pulse 1.6s ease-out infinite',
};

export default function StageList({ stages = [], elapsedSec, className = '' }) {
  return (
    <div className={className}>
      {/* Inline keyframes — scoped via the unique animation name. */}
      <style>{`
        @keyframes stage-pulse {
          0%   { transform: scale(0.8); opacity: 0.55; }
          80%  { transform: scale(2.2); opacity: 0; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes stage-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      {typeof elapsedSec === 'number' && elapsedSec > 0 && (
        <div className="text-xs text-faint mb-3 tabular-nums">
          ~{elapsedSec}s elapsed
        </div>
      )}

      <ul className="relative" aria-live="polite">
        {/* Connector line — sits behind the dots, runs from the first
            dot center to the last. Length is computed from row count. */}
        <span
          aria-hidden
          className="absolute left-[3px] top-1.5 w-px bg-surface-subtle"
          style={{ bottom: '0.375rem' }}
        />

        {stages.map((s) => {
          const isActive = s.state === 'active';
          const showBar = isActive && s.progress && s.progress.total > 0;
          const pct = showBar
            ? Math.min(100, Math.max(0, (s.progress.done / s.progress.total) * 100))
            : 0;
          return (
            <li key={s.id} className="relative pl-6 pb-3 last:pb-0">
              {/* Dot + ring-pulse on active. The dot is positioned
                  exactly on the connector line. */}
              <span className="absolute left-0 top-[5px] flex items-center justify-center w-[7px] h-[7px]">
                <LevelDot color={dotColor(s.state)} size="md" />
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-full bg-status-info"
                    style={pulseRingStyle}
                  />
                )}
              </span>

              <div className="flex items-center gap-3 text-sm leading-tight">
                <span className={`flex-1 ${rowText(s.state)}`}>{s.label}</span>
                {s.progress && (
                  <span className="tabular-nums text-xs text-faint">
                    {s.progress.done} / {s.progress.total}
                  </span>
                )}
              </div>

              {/* Active-row detail sub-line. Quiet, single line. */}
              {isActive && s.detail && (
                <div className="mt-1 text-xs text-mutedink truncate">
                  {s.detail}
                </div>
              )}

              {/* Progress bar — appears only on the active row when we
                  know totals. Filled portion + a shimmer band over the
                  unfilled remainder so it reads as "still working". */}
              {showBar && (
                <div className="mt-2 h-1 w-full rounded-full bg-surface-subtle overflow-hidden">
                  <div
                    className="h-full bg-status-info transition-[width] duration-500 ease-out"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}
              {isActive && !showBar && (
                // Indeterminate shimmer for active stages without
                // progress totals (picking, polishing).
                <div className="mt-2 h-1 w-full rounded-full bg-surface-subtle overflow-hidden relative">
                  <div
                    className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-status-info/60 to-transparent"
                    style={{ animation: 'stage-shimmer 1.6s ease-in-out infinite' }}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
